import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createContext } from 'use-context-selector';

import {
  DocumentComponents,
  DocumentNode,
  DocumentPath,
  EditorCommand,
} from '../../types';

type DisplayMode = 'edit' | 'view';
export type EditorType = 'editor' | 'viewer';

export interface EditorState {
  document: DocumentNode;
  cursor: DocumentPath;
  tempCursor?: DocumentPath;
  displayMode: DisplayMode;
  editorType: EditorType;
  displayDate?: Date;
}

export interface EditorContextData {
  components: DocumentComponents;
  newDocument: DocumentNode;
  state: EditorState;
  dispatch(command: EditorCommand): void;
  getState(): EditorState;
  setState(value: EditorState | ((prevState: EditorState) => void)): void;
}

export const EditorContext = createContext<EditorContextData | null>(null);

export interface EditorContextProviderProps {
  value: DocumentNode;
  onChange?: (value: DocumentNode) => void;
  newValue: DocumentNode;
  editorType: EditorType;
  components: DocumentComponents;
  displayDate?: Date;
  children: React.ReactNode;
}

export function EditorContextProvider(
  props: EditorContextProviderProps,
): React.ReactElement {
  const { value, newValue, editorType, components, displayDate, children } = props;

  const propsRef = useRef<EditorContextProviderProps>(props);
  propsRef.current = props;

  const [innerState, setInnerState] = useState<
  Omit<EditorState, 'document' | 'title'>
  >(() => ({
    cursor: [],
    displayMode: editorType === 'editor' ? 'edit' : 'view',
    editorType,
    displayDate,
  }));
  const state = useMemo(
    () => ({
      ...innerState,
      document: value,
    }),
    [innerState, value],
  );

  const stateRef = useRef<EditorState>(state);
  stateRef.current = state;
  const getState = useCallback(() => stateRef.current, []);

  const setState = useCallback(
    (value: EditorState | ((value: EditorState) => EditorState)) => {
      let nextState: EditorState;
      if (typeof value === 'function') {
        nextState = value(stateRef.current);
      } else {
        nextState = value;
      }
      setInnerState({
        cursor: nextState.cursor,
        tempCursor: nextState.tempCursor,
        displayMode: nextState.displayMode,
        editorType: nextState.editorType,
        displayDate: nextState.displayDate,
      });
      if (propsRef.current.value !== nextState.document) {
        propsRef.current.onChange?.(nextState.document);
      }
    },
    [],
  );
  const contextDataRef = useRef<EditorContextData>(null!);
  const dispatch = useCallback((command: EditorCommand) => {
    command.exec(contextDataRef.current);
  }, []);
  const contextData: EditorContextData = {
    components,
    state,
    getState,
    setState,
    dispatch,
    newDocument: newValue,
  };
  contextDataRef.current = contextData;
  return (
    <EditorContext.Provider value={contextData}>
      { children }
    </EditorContext.Provider>
  );
}
